import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, IndianRupee, MapPin, Package, Tag, Phone } from 'lucide-react';

const categories = ['Rice', 'Wheat', 'Corn', 'Pulses', 'Vegetables', 'Fruits', 'Other'];

const SellProduct = () => {
  const [sellPosts, setSellPosts] = useState([]);
  const [form, setForm] = useState({ type: 'Rice', otherName: '', quantity: '', price: '', address: '', phone: '', altPhone: '', image: null });
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const s = localStorage.getItem('marketplaceSellPosts');
    if (s) setSellPosts(JSON.parse(s));
  }, []);

  useEffect(() => {
    localStorage.setItem('marketplaceSellPosts', JSON.stringify(sellPosts));
  }, [sellPosts]);

  const onFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      setForm(f => ({ ...f, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const submitSell = e => {
    e.preventDefault();
    const otherOk = form.type !== 'Other' || !!(form.otherName || '').trim();
    if (!form.type || !otherOk || !(form.quantity || '').trim() || !(form.price || '').trim() || !(form.address || '').trim() || !(form.phone || '').trim()) return;
    const item = { id: Date.now(), createdAt: new Date().toISOString(), ...form };
    setSellPosts(p => [item, ...p]);
    setForm({ type: 'Rice', otherName: '', quantity: '', price: '', address: '', phone: '', altPhone: '', image: null });
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top to see success (or feed)
  };

  return (
    <div className="min-h-screen bg-transparent py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black text-slate-800 mb-2">Sell Your Produce</h1>
          <p className="text-slate-600 text-lg">Reach thousands of buyers directly with no middlemen.</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="bg-emerald-600 p-6 sm:p-10 text-white">
            <h2 className="text-2xl font-bold flex items-center">
              <Package className="w-6 h-6 mr-3" />
              Create New Listing
            </h2>
            <p className="text-emerald-100 mt-2">Fill in the details below to post your product to the marketplace.</p>
          </div>

          <form onSubmit={submitSell} className="p-8 sm:p-10 space-y-8">
            {/* Product Details Section */}
            <div>
              <h3 className="text-slate-800 font-bold mb-4 flex items-center uppercase text-sm tracking-wider"><Tag className="w-4 h-4 mr-2 text-emerald-500" /> Product Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                  <div className="relative">
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full appearance-none bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition">
                      {categories.map(c => (<option key={c} value={c}>{c}</option>))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                    </div>
                  </div>
                </div>

                {form.type === 'Other' && (
                  <div className="animate-fade-in">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Specific Product Name</label>
                    <input value={form.otherName} onChange={e => setForm(f => ({ ...f, otherName: e.target.value }))} placeholder="e.g. Mustard Seeds" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Quantity Available</label>
                  <div className="relative">
                    <input value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="e.g. 500 kg" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                    <Package className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Expected Price (₹)</label>
                  <div className="relative">
                    <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="e.g. 2500 per quintal" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                    <IndianRupee className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 my-6"></div>

            {/* Location & Contact Section */}
            <div>
              <h3 className="text-slate-800 font-bold mb-4 flex items-center uppercase text-sm tracking-wider"><MapPin className="w-4 h-4 mr-2 text-emerald-500" /> Location & Contact</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Pickup Address</label>
                  <div className="relative">
                    <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Village, Taluka, District, State - PIN" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                    <MapPin className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Primary Phone</label>
                    <div className="relative">
                      <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="10-digit mobile number" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                      <Phone className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Alt Phone (Optional)</label>
                    <div className="relative">
                      <input value={form.altPhone} onChange={e => setForm(f => ({ ...f, altPhone: e.target.value }))} placeholder="Alternative contact" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition" />
                      <Phone className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 my-6"></div>

            {/* Image Upload */}
            <div>
              <h3 className="text-slate-800 font-bold mb-4 flex items-center uppercase text-sm tracking-wider"><ImageIcon className="w-4 h-4 mr-2 text-emerald-500" /> Product Photo</h3>
              <div
                className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all ${preview ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <div className="relative w-full max-w-sm">
                    <img src={preview} alt="Preview" className="w-full rounded-lg shadow-md" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/40 rounded-lg">
                      <span className="text-white font-bold">Change Photo</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                    <p className="text-slate-600 font-medium">Click to upload a photo</p>
                    <p className="text-slate-400 text-sm mt-1">JPG, PNG up to 5MB</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={!form.type || (form.type === 'Other' && !(form.otherName || '').trim()) || !(form.quantity || '').trim() || !(form.price || '').trim() || !(form.address || '').trim() || !(form.phone || '').trim()}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-xl hover:shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
              >
                Post Listing
              </button>
              <p className="text-center text-slate-400 text-xs mt-4">By posting, you agree to our marketplace terms and conditions.</p>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default SellProduct;
